import {useSearchParams} from 'react-router-dom';
import {SocialAnalytics} from '../../components/marketing/SocialAnalytics';
import {SocialAccountReadiness} from '../../components/marketing/SocialAccountReadiness';
import {SocialDeliveryStatus} from '../../components/marketing/SocialDeliveryStatus';
import {SocialPostPlanner} from '../../components/marketing/SocialPostPlanner';
import {emptySocialFilters} from '../../types/socialMedia';
import React, { useState, useEffect, useCallback } from 'react';
import {format, startOfMonth, endOfMonth, startOfWeek, endOfWeek} from 'date-fns';
import {loadSocialCalendar} from '../../components/marketing/socialPostData';
import {
  AspectRatio,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Tab,
  TabList,
  Tabs,
  TabPanel,
  Alert,
  Switch,
  FormControl,
  FormLabel,
  IconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Divider,
  ListItemDecorator,
  ListItemContent,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  Table,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Clock as ScheduleIcon,
  Calendar as CalendarIcon,
  UserCircle as AccountIcon,
  Palette as TemplatesIcon,
  Gear as SettingsIcon,
  FacebookLogo as FacebookIcon,
  InstagramLogo as InstagramIcon,
  Buildings as BusinessIcon,
  Robot as AIIcon,
  LinkBreak as LinkOffIcon,
  LinkSimple as LinkIcon,
  CaretDown as ExpandMoreIcon,
  Star as StarIcon,
  Star as StarBorderIcon,
  Quotes as QuoteIcon,
  ArrowsLeftRight as CompareIcon,
  Image as ImageIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';
import { SocialMediaCalendar } from '../../components/marketing/SocialMediaCalendar';
import { SocialMediaPostComposer } from '../../components/marketing/SocialMediaPostComposer';
import { SocialAccountConnector } from '../../components/marketing/SocialAccountConnector';
import { SocialAutoSettingsModal } from '../../components/marketing/SocialAutoSettingsModal';
import type { MarketingSettings, ImageTemplate, ImageTemplateCategory } from '../../types/marketing';

// Template Card component for the Templates tab
const TemplateCard: React.FC<{
  template: ImageTemplate;
  onToggleFavorite: () => void;
}> = ({ template, onToggleFavorite }) => {
  const CATEGORY_INFO: Record<ImageTemplateCategory, { label: string; color: string }> = {
    testimonial: { label: 'Testimonial', color: 'primary' },
    company_post: { label: 'Company', color: 'success' },
    before_after: { label: 'Before/After', color: 'warning' },
    promotion: { label: 'Promotion', color: 'danger' },
    service: { label: 'Service', color: 'neutral' }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        '&:hover': {
          boxShadow: 'md',
          borderColor: 'primary.300'
        }
      }}
    >
      {/* Favorite Button */}
      <IconButton
        size="sm"
        variant="plain"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite();
        }}
      >
        {template.is_favorite ? (
          <StarIcon color="var(--joy-palette-warning-400)" />
        ) : (
          <StarBorderIcon style={{ opacity: 0.5 }} />
        )}
      </IconButton>

      {/* Template Preview */}
      <AspectRatio ratio="1" sx={{ mb: 1 }}>
        {template.preview_image_url ? (
          <Box
            component="img"
            src={template.preview_image_url}
            alt={template.name}
            sx={{
              objectFit: 'cover',
              width: '100%',
              height: '100%'
            }}
          />
        ) : (
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}
          >
            <ImageIcon color="#00bcd4" size={32} style={{ marginBottom: 8 }} />
            <Typography
              level="body-xs"
              sx={{ color: '#fff', textAlign: 'center' }}
            >
              {template.name}
            </Typography>
          </Box>
        )}
      </AspectRatio>

      {/* Template Info */}
      <Box sx={{ px: 1, pb: 1 }}>
        <Typography
          level="body-sm"
          fontWeight="lg"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {template.name}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5
          }}
        >
          <Chip
            size="sm"
            variant="soft"
            color={CATEGORY_INFO[template.category]?.color as any || 'neutral'}
          >
            {template.category_display || CATEGORY_INFO[template.category]?.label}
          </Chip>
          {template.usage_count && template.usage_count > 0 && (
            <Typography
              level="body-xs"
              sx={{ color: 'text.tertiary' }}
            >
              Used {template.usage_count}x
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export const SocialMedia: React.FC = () => {
  const queryClient = useQueryClient();
  const [tabParams, setTabParams] = useSearchParams();
  const requestedTab = tabParams.get('tab') || 'calendar';
  const activeTab = ['calendar','posts','templates','analytics'].includes(requestedTab) ? requestedTab : 'calendar';
  const setActiveTab = (tab: string) => setTabParams(previous => {const next = new URLSearchParams(previous);next.set('tab', tab);return next;});
  const [postPage, setPostPage] = useState(1);
  const [postFilters, setPostFilters] = useState({...emptySocialFilters});
  const postParams = new URLSearchParams(Object.entries(postFilters).filter(([,v])=>v));
  postParams.set('page', String(postPage));
  const [calendarRange, setCalendarRange] = useState(() => ({start: format(startOfWeek(startOfMonth(new Date())), 'yyyy-MM-dd'), end: format(endOfWeek(endOfMonth(new Date())), 'yyyy-MM-dd')}));
  const handleRangeChange = useCallback((start: string, end: string) => setCalendarRange({start, end}), []);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerDate, setComposerDate] = useState<Date | undefined>(undefined);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [accountConnectorOpen, setAccountConnectorOpen] = useState(false);
  const [showAutoSettingsModal, setShowAutoSettingsModal] = useState(false);
  const [generationTaskId, setGenerationTaskId] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);

  // Post stats/metrics modal state
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsPost, setStatsPost] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsData, setStatsData] = useState<Record<string, any> | null>(null);

  // Fetch social media accounts
  const { data: accounts, isLoading: accountsLoading, isError: accountsError, refetch: retryAccounts } = useQuery({
    queryKey: ['social-accounts'],
    queryFn: async () => {
      const response = await api.getAllPages('/api/marketing/social-accounts/');
      const results = response.data;
      console.log('Social accounts:', results);
      return results;
    }
  });

  const postQuery = useQuery({
    queryKey: ['social-posts', 'list', postPage, postFilters],
    queryFn: async () => (await api.get<any>(`/api/marketing/social-posts/?${postParams}`)).data,
    enabled: activeTab === 'posts',
    refetchInterval: activeTab === 'posts' ? 10000 : false,
  });
  const calendarQuery = useQuery({
    queryKey: ['social-posts', 'calendar', calendarRange],
    queryFn: () => loadSocialCalendar(calendarRange.start, calendarRange.end),
    enabled: activeTab === 'calendar',
    refetchInterval: activeTab === 'calendar' ? 10000 : false,
  });
  const posts: any[] = postQuery.data?.results || [];
  const counts = postQuery.data?.status_counts || {};
  useEffect(() => {
    if (postPage > 1 && ((postQuery.error as any)?.response?.status === 404 || (postQuery.data && !posts.length))) setPostPage(page => page - 1);
  }, [postPage, postQuery.isError, postQuery.data, posts.length]);

  // Fetch marketing settings
  const { data: marketingSettings, isError: settingsError, refetch: retrySettings } = useQuery<MarketingSettings | null>({
    queryKey: ['marketing-settings'],
    queryFn: async () => {
      return (await api.get<MarketingSettings>('/api/marketing/settings/')).data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch available templates with org preferences
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['image-templates', 'available'],
    queryFn: async () => {
      const response = await api.get('/api/marketing/image-templates/available/');
      return response.data as ImageTemplate[];
    }
  });

  // Toggle favorite mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({
      templateId,
      preferenceId
    }: {
      templateId: number;
      preferenceId?: number;
    }) => {
      if (preferenceId) {
        await api.post(
          `/api/marketing/template-preferences/${preferenceId}/toggle-favorite/`,
          {}
        );
      } else {
        await api.post('/api/marketing/template-preferences/', {
          template: templateId,
          is_enabled: true,
          is_favorite: true
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-templates'] });
    }
  });

  // Toggle auto-generation mutation
  const toggleAutoGenerationMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      // First get the settings to get the ID
      const settingsResponse = await api.get<MarketingSettings>('/api/marketing/settings/');
      const settingsId = settingsResponse.data.id;

      // Then patch with the ID
      const response = await api.patch(`/api/marketing/settings/${settingsId}/`, {
        social_auto_generate: enabled
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-settings'] });
    },
    onError: () => toast.error('Could not update AI generation. Try again.'),
  });

  // Auto-generate posts mutation
  const autoGenerateMutation = useMutation<{ success: boolean; posts?: any[] }>({
    mutationFn: async () => {
      const response = await api.post<{ success: boolean; posts?: any[] }>('/api/marketing/social-posts/auto-generate/', {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] });
    }
  });

  // Get platform icon
  const getPlatformIcon = (platform: string) => {
    switch(platform) {
      case 'facebook':
        return <FacebookIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'google_business_profile':
        return <BusinessIcon />;
      default:
        return <AccountIcon />;
    }
  };

  const handleCreatePost = (date?: Date) => {
    setComposerDate(date);
    setEditingPost(null);
    setComposerOpen(true);
  };

  const handleEditPost = (post: any) => {
    setEditingPost(post);
    setComposerDate(undefined);
    setComposerOpen(true);
  };

  const handleApprovePost = async (post: any) => {
    try {
      // Call the approve endpoint to change status from pending_review to scheduled
      await api.post(`/api/marketing/social-posts/${post.id}/approve/`, {});
      
      // Refresh the posts list
      await queryClient.invalidateQueries({ queryKey: ['social-posts'] });
      
      // Show success message
      toast.success(`Post scheduled for ${new Date(post.scheduled_time).toLocaleString()}`);
    } catch (error) {
      console.error('Failed to approve post:', error);
      toast.error('Could not schedule this post. Open Edit to check its date and destination accounts.');
    }
  };

  const handleAutoGenerate = async (useAsync: boolean = false) => {
    if (!accounts?.some((account: any)=>account.is_active)) {
      alert('Please connect your social media accounts first before generating posts.');
      return;
    }

    if (useAsync) {
      // Use async generation for more photos without timeout
      try {
        const response = await api.post<{success: boolean; task_id?: string; message?: string}>(
          '/api/marketing/social-posts/auto-generate-async/',
          { use_all_photos: true }
        );
        
        if (response.data.success && response.data.task_id) {
          setGenerationTaskId(response.data.task_id);
          setGenerationStatus('pending');
          alert('Generation started! This may take a minute. We\'ll notify you when posts are ready.');
          
          // Start polling for status
          checkGenerationStatus(response.data.task_id);
        } else {
          alert(response.data.message || 'Failed to start generation.');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Failed to start generation. Please try again.';
        alert(errorMessage);
      }
    } else {
      // Use sync generation for quick results (3 photos)
      try {
        const result = await autoGenerateMutation.mutateAsync();
        if (result.success) {
          alert(`Successfully generated ${result.posts?.length || 0} social media posts!`);
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Failed to generate posts. Please try again.';
        alert(errorMessage);
      }
    }
  };
  
  const checkGenerationStatus = async (taskId: string) => {
    try {
      const response = await api.get<{status: string; posts_created?: number; message?: string}>(
        `/api/marketing/social-posts/check-generation-status/?task_id=${taskId}`
      );
      
      setGenerationStatus(response.data.status);
      
      if (response.data.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: ['social-posts'] });
        alert(`Successfully generated ${response.data.posts_created || 0} social media posts!`);
        setGenerationTaskId(null);
        setGenerationStatus(null);
      } else if (response.data.status === 'failed') {
        alert('Generation failed. Please try again.');
        setGenerationTaskId(null);
        setGenerationStatus(null);
      } else if (response.data.status === 'pending' || response.data.status === 'started' || response.data.status === 'retrying') {
        // Continue polling
        setTimeout(() => checkGenerationStatus(taskId), 3000);
      }
    } catch (error) {
      console.error('Error checking generation status:', error);
      setGenerationTaskId(null);
      setGenerationStatus(null);
    }
  };

  const handleViewStats = async (post: any) => {
    setStatsPost(post);
    setStatsData(null);
    setStatsError(null);
    setStatsLoading(true);
    setStatsModalOpen(true);
    try {
      const response = await api.get<Record<string, any>>(
        `/api/marketing/social-posts/${post.id}/metrics/`
      );
      setStatsData(response.data || {});
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to load engagement metrics. Please try again.';
      setStatsError(message);
      toast.error(message);
    } finally {
      setStatsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Actions */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, md: 2 },
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
          }}
        >
          <Button
            startDecorator={<AddIcon />}
            size="lg"
            color="primary"
            onClick={() => handleCreatePost()}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create Post
          </Button>

          {/* Auto-generation toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: { xs: 1, md: 3 },
              ml: { xs: 0, md: 2 },
            }}
          >
            <FormControl orientation="horizontal" sx={{ gap: 1 }}>
              <Switch
                checked={marketingSettings?.social_auto_generate || false}
                onChange={(event) => {
                  if (event.target.checked && !accounts?.some((account: any)=>account.is_active)) {
                    alert('Please connect your social media accounts first before enabling auto-generation.');
                    return;
                  }
                  toggleAutoGenerationMutation.mutate(event.target.checked);
                }}
                disabled={toggleAutoGenerationMutation.isPending}
                startDecorator={<AIIcon />}
                endDecorator={
                  marketingSettings?.social_auto_generate && (
                    <IconButton
                      size="sm"
                      variant="plain"
                      onClick={() => setShowAutoSettingsModal(true)}
                    >
                      <SettingsIcon />
                    </IconButton>
                  )
                }
              />
              <FormLabel>Auto-Generate Social Posts</FormLabel>
            </FormControl>
            
            {marketingSettings?.social_auto_generate && (
              <Chip
                size="sm"
                color="success"
                variant="soft"
              >
                {marketingSettings.generation_frequency === 'daily' ? 'Daily' :
                 marketingSettings.generation_frequency === 'weekly' ? 'Weekly' :
                 marketingSettings.generation_frequency === 'biweekly' ? 'Twice Weekly' : 
                 'Custom Schedule'}
              </Chip>
            )}
            
            {/* Auto-generate now button */}
            {marketingSettings?.social_auto_generate && (
              <>
                <Button
                  variant="soft"
                  size="sm"
                  startDecorator={<AIIcon />}
                  onClick={() => handleAutoGenerate(false)}
                  loading={autoGenerateMutation.isPending || generationStatus === 'pending' || generationStatus === 'started'}
                  disabled={!accounts?.some((account: any)=>account.is_active)}
                >
                  Quick Generate (3 posts)
                </Button>
                {/* Temporarily disable async option if Celery is not configured
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<AIIcon />}
                  onClick={() => handleAutoGenerate(true)}
                  loading={generationStatus === 'pending' || generationStatus === 'started'}
                  disabled={!accounts?.some((account: any)=>account.is_active)}
                >
                  Full Generate (10 posts)
                </Button>
                */}
              </>
            )}
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' } }}>
          <Dropdown>
            <MenuButton
              startDecorator={<AccountIcon />}
              endDecorator={<ExpandMoreIcon />}
              variant="outlined"
              size="sm"
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              Manage Accounts
            </MenuButton>
            <Menu placement="bottom-end" sx={{ minWidth: 250 }}>
              {/* Facebook */}
              {(() => {
                const fbAccount = accounts?.find((acc: any) => acc.platform === 'facebook');
                return fbAccount ? (
                  <MenuItem
                    onClick={async () => {
                      if (window.confirm(`Disconnect ${fbAccount.account_name}?`)) {
                        try {
                          await api.delete(`/api/marketing/social-accounts/${fbAccount.id}/`);
                          queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
                        } catch (error) {
                          console.error('Failed to disconnect account:', error);
                        }
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <FacebookIcon color="#1877F2" />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">{fbAccount.account_name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'success.600' }}>Connected</Typography>
                    </ListItemContent>
                    <LinkOffIcon size={18} color="var(--joy-palette-danger-500)" style={{ marginLeft: 'auto' }} />
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => setAccountConnectorOpen(true)}>
                    <ListItemDecorator>
                      <FacebookIcon color="#1877F2" />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">Facebook</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Not connected</Typography>
                    </ListItemContent>
                    <LinkIcon size={18} color="var(--joy-palette-primary-500)" style={{ marginLeft: 'auto' }} />
                  </MenuItem>
                );
              })()}
              
              <Divider />
              
              {/* Instagram */}
              {(() => {
                const igAccount = accounts?.find((acc: any) => acc.platform === 'instagram');
                return igAccount ? (
                  <MenuItem
                    onClick={async () => {
                      if (window.confirm(`Disconnect ${igAccount.account_name}?`)) {
                        try {
                          await api.delete(`/api/marketing/social-accounts/${igAccount.id}/`);
                          queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
                        } catch (error) {
                          console.error('Failed to disconnect account:', error);
                        }
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <InstagramIcon color="#E4405F" />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">{igAccount.account_name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'success.600' }}>Connected</Typography>
                    </ListItemContent>
                    <LinkOffIcon size={18} color="var(--joy-palette-danger-500)" style={{ marginLeft: 'auto' }} />
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => setAccountConnectorOpen(true)}>
                    <ListItemDecorator>
                      <InstagramIcon color="#E4405F" />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">Instagram</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Not connected</Typography>
                    </ListItemContent>
                    <LinkIcon size={18} color="var(--joy-palette-primary-500)" style={{ marginLeft: 'auto' }} />
                  </MenuItem>
                );
              })()}
              
              <Divider />
              
              {/* Google Business Profile */}
              {(() => {
                const gbpAccount = accounts?.find((acc: any) => acc.platform === 'google_business_profile');
                return gbpAccount ? (
                  <MenuItem
                    onClick={async () => {
                      if (window.confirm(`Disconnect ${gbpAccount.account_name}?`)) {
                        try {
                          await api.delete(`/api/marketing/social-accounts/${gbpAccount.id}/`);
                          queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
                        } catch (error) {
                          console.error('Failed to disconnect account:', error);
                        }
                      }
                    }}
                  >
                    <ListItemDecorator>
                      <BusinessIcon color="#4285F4" />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">{gbpAccount.account_name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'success.600' }}>Connected</Typography>
                    </ListItemContent>
                    <LinkOffIcon size={18} color="var(--joy-palette-danger-500)" style={{ marginLeft: 'auto' }} />
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => setAccountConnectorOpen(true)}>
                    <ListItemDecorator>
                      <BusinessIcon color="#4285F4" />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">Google Business Profile</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Not connected</Typography>
                    </ListItemContent>
                    <LinkIcon size={18} color="var(--joy-palette-primary-500)" style={{ marginLeft: 'auto' }} />
                  </MenuItem>
                );
              })()}
              
              <Divider />
              
              {/* Manage All Option */}
              <MenuItem onClick={() => setAccountConnectorOpen(true)}>
                <ListItemDecorator>
                  <SettingsIcon />
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">Manage All Accounts</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>View detailed settings</Typography>
                </ListItemContent>
              </MenuItem>
            </Menu>
          </Dropdown>
        </Box>
      </Box>

      {accountsLoading && <Typography>Loading accounts…</Typography>}
      {accountsError && <Alert color="danger" endDecorator={<Button onClick={() => retryAccounts()}>Retry</Button>}>Unable to load social accounts.</Alert>}
      {settingsError && <Alert color="danger" endDecorator={<Button onClick={() => retrySettings()}>Retry</Button>}>Unable to load automation settings.</Alert>}
      {!accountsError && !accountsLoading && <SocialAccountReadiness accounts={accounts || []} settings={marketingSettings} onManage={()=>setAccountConnectorOpen(true)} onSettings={()=>setShowAutoSettingsModal(true)}/>}
      {/* Main Content */}
      <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value as string)} sx={{ width: '100%' }}>
        <TabList
          sx={{
            width: '100%',
            flexWrap: 'wrap',
            '& > *': { flex: { xs: '1 1 45%', sm: '0 0 auto' } },
          }}
        >
          <Tab value="calendar">
            <CalendarIcon style={{ marginRight: 8 }} />
            Calendar
          </Tab>
          <Tab value="posts">
            <ScheduleIcon style={{ marginRight: 8 }} />
            Posts
          </Tab>
          <Tab value="templates">
            <TemplatesIcon style={{ marginRight: 8 }} />
            Templates
          </Tab>
          <Tab value="analytics">
            <SettingsIcon style={{ marginRight: 8 }} />
            Analytics
          </Tab>
        </TabList>

        {/* Calendar Tab */}
        <TabPanel value="calendar" sx={{ p: 0, mt: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="h3" sx={{ mb: 2 }}>Content Calendar</Typography>
              {calendarQuery.isPending && <Typography>Loading calendar…</Typography>}
              {calendarQuery.isError ? <Alert color="danger" endDecorator={<Button onClick={() => calendarQuery.refetch()}>Retry</Button>}>Unable to load the complete calendar.</Alert> : <SocialMediaCalendar
                posts={calendarQuery.data || []}
                onRangeChange={handleRangeChange}
                onCreatePost={handleCreatePost}
                onEditPost={handleEditPost}
              />}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Posts Tab */}
        <TabPanel value="posts" sx={{ p: 0, mt: 3 }}>
          {postQuery.isPending && <Typography>Loading posts…</Typography>}
          {postQuery.isError && <Alert color="danger" endDecorator={<Button onClick={() => postQuery.refetch()}>Retry</Button>}>Unable to load posts.</Alert>}
          {!postQuery.isPending && !postQuery.isError && !posts.length && <Alert>No posts match this view. Clear filters or create a post.</Alert>}
          <Box sx={{display:'flex',gap:1,flexWrap:'wrap',mb:2}}>{Object.entries(counts).map(([status,count])=><Chip key={status}>{status.replace('_',' ')}: {String(count)}</Chip>)}</Box>
          <SocialPostPlanner posts={posts} filters={postFilters} onFiltersChange={filters=>{setPostFilters(filters);setPostPage(1);}} accounts={accounts || []} onEdit={handleEditPost} onApprove={handleApprovePost} onStats={handleViewStats} renderRecovery={post=><SocialDeliveryStatus post={post}/>}/>
          <Box sx={{display:'flex', gap:2, alignItems:'center', flexWrap:'wrap', mt:2}}>
            <Button disabled={postPage === 1 || postQuery.isFetching} onClick={() => setPostPage(p => p - 1)}>Previous</Button>
            <Typography>Page {postPage} · {postQuery.data?.count || 0} posts total</Typography>
            <Button disabled={!postQuery.data?.next || postQuery.isFetching} onClick={() => setPostPage(p => p + 1)}>Next</Button>
          </Box>
        </TabPanel>


        {/* Templates Tab */}
        <TabPanel value="templates" sx={{ p: 0, mt: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography level="h3">Image Templates</Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Browse and favorite templates for branded social media images
                  </Typography>
                </Box>
              </Box>

              {templatesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !templates || templates.length === 0 ? (
                <Alert color="warning">
                  No templates are available yet. Contact support for help setting up your template library.
                </Alert>
              ) : (
                <>
                  {/* Favorites Section */}
                  {templates.some(t => t.is_favorite) && (
                    <Box sx={{ mb: 4 }}>
                      <Typography level="title-lg" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StarIcon color="var(--joy-palette-warning-400)" />
                        Favorites
                      </Typography>
                      <Grid container spacing={2}>
                        {templates.filter(t => t.is_favorite).map((template) => (
                          <Grid key={template.id} xs={6} sm={4} md={3} lg={2}>
                            <TemplateCard
                              template={template}
                              onToggleFavorite={() => toggleFavoriteMutation.mutate({ templateId: template.id })}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ my: 3 }} />
                    </Box>
                  )}

                  {/* Testimonials */}
                  {templates.some(t => t.category === 'testimonial') && (
                    <Box sx={{ mb: 4 }}>
                      <Typography level="title-lg" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QuoteIcon />
                        Testimonials
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        Turn customer reviews into engaging visual testimonials
                      </Typography>
                      <Grid container spacing={2}>
                        {templates.filter(t => t.category === 'testimonial' && !t.is_favorite).map((template) => (
                          <Grid key={template.id} xs={6} sm={4} md={3} lg={2}>
                            <TemplateCard
                              template={template}
                              onToggleFavorite={() => toggleFavoriteMutation.mutate({ templateId: template.id })}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Company Posts */}
                  {templates.some(t => t.category === 'company_post') && (
                    <Box sx={{ mb: 4 }}>
                      <Typography level="title-lg" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        Company Posts
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        Professional branded images for company announcements and updates
                      </Typography>
                      <Grid container spacing={2}>
                        {templates.filter(t => t.category === 'company_post' && !t.is_favorite).map((template) => (
                          <Grid key={template.id} xs={6} sm={4} md={3} lg={2}>
                            <TemplateCard
                              template={template}
                              onToggleFavorite={() => toggleFavoriteMutation.mutate({ templateId: template.id })}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Before & After */}
                  {templates.some(t => t.category === 'before_after') && (
                    <Box sx={{ mb: 4 }}>
                      <Typography level="title-lg" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompareIcon />
                        Before & After
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        Showcase project transformations with comparison layouts
                      </Typography>
                      <Grid container spacing={2}>
                        {templates.filter(t => t.category === 'before_after' && !t.is_favorite).map((template) => (
                          <Grid key={template.id} xs={6} sm={4} md={3} lg={2}>
                            <TemplateCard
                              template={template}
                              onToggleFavorite={() => toggleFavoriteMutation.mutate({ templateId: template.id })}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Promotions */}
                  {templates.some(t => t.category === 'promotion') && (
                    <Box sx={{ mb: 4 }}>
                      <Typography level="title-lg" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon />
                        Promotions
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        Eye-catching templates for special offers and promotions
                      </Typography>
                      <Grid container spacing={2}>
                        {templates.filter(t => t.category === 'promotion' && !t.is_favorite).map((template) => (
                          <Grid key={template.id} xs={6} sm={4} md={3} lg={2}>
                            <TemplateCard
                              template={template}
                              onToggleFavorite={() => toggleFavoriteMutation.mutate({ templateId: template.id })}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Services */}
                  {templates.some(t => t.category === 'service') && (
                    <Box sx={{ mb: 4 }}>
                      <Typography level="title-lg" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon />
                        Services
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        Highlight your services with professional templates
                      </Typography>
                      <Grid container spacing={2}>
                        {templates.filter(t => t.category === 'service' && !t.is_favorite).map((template) => (
                          <Grid key={template.id} xs={6} sm={4} md={3} lg={2}>
                            <TemplateCard
                              template={template}
                              onToggleFavorite={() => toggleFavoriteMutation.mutate({ templateId: template.id })}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value="analytics" sx={{ p: 0, mt: 3 }}>
          {activeTab === 'analytics' && <SocialAnalytics/>}
        </TabPanel>
      </Tabs>

      {/* Post Composer Modal */}
      <SocialMediaPostComposer
        open={composerOpen}
        onClose={() => {
          setComposerOpen(false);
          setComposerDate(undefined);
          setEditingPost(null);
        }}
        initialDate={composerDate}
        editingPost={editingPost}
      />

      {/* Account Connector Modal */}
      <SocialAccountConnector
        open={accountConnectorOpen}
        onClose={() => setAccountConnectorOpen(false)}
      />

      {/* Social Auto Settings Modal */}
      <SocialAutoSettingsModal
        open={showAutoSettingsModal}
        onClose={() => setShowAutoSettingsModal(false)}
        settings={marketingSettings || null}
        onSave={async (updatedSettings) => {
          try {
            // First get the settings to get the ID
            const settingsResponse = await api.get<MarketingSettings>('/api/marketing/settings/');
            const settingsId = settingsResponse.data.id;

            // Then patch with the ID
            await api.patch(`/api/marketing/settings/${settingsId}/`, updatedSettings);
            queryClient.invalidateQueries({ queryKey: ['marketing-settings'] });
            setShowAutoSettingsModal(false);
          } catch (error) {
            console.error('Error saving settings:', error);
          }
        }}
      />

      {/* Post Engagement Stats Modal */}
      <Modal open={statsModalOpen} onClose={() => setStatsModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 560, width: '100%' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 0.5 }}>
            Engagement Stats
          </Typography>
          {statsPost?.title && (
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
              {statsPost.title}
            </Typography>
          )}

          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : statsError ? (
            <Alert color="danger">{statsError}</Alert>
          ) : !statsData || Object.keys(statsData).length === 0 ? (
            <Alert color="neutral">
              No engagement metrics are available for this post yet.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Object.entries(statsData).map(([platform, metrics]) => (
                <Card key={platform} variant="outlined">
                  <CardContent>
                    <Typography
                      level="title-sm"
                      sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      {getPlatformIcon(platform)}
                      {platform
                        .split('_')
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')}
                    </Typography>
                    {metrics && (metrics as any).error ? (
                      <Alert color="warning" size="sm">
                        {(metrics as any).error}
                      </Alert>
                    ) : metrics && Object.keys(metrics).length > 0 ? (
                      <Table size="sm" borderAxis="none">
                        <tbody>
                          {Object.entries(metrics as Record<string, any>).map(([key, value]) => (
                            <tr key={key}>
                              <td style={{ textTransform: 'capitalize', color: 'var(--joy-palette-text-secondary)' }}>
                                {key.replace(/_/g, ' ')}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        No metrics reported.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};